import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Grid, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { AgentNetCheckBox } from 'ui-kit/inputs';

interface OptionType {
  id: number;
  name: string;
}

interface BopsPreferencesChecklistProps {
  options: OptionType[];
  /**
   * Currently-selected preferences. Index 0 is a server-provided placeholder (e.g. "PDF/Doc (Default)",
   * id 0) that is always checked/disabled, preserved across changes, and dropped on save; the remaining
   * entries are the user's selections.
   */
  value?: OptionType[];
  onChange: (value: OptionType[]) => void;
  qaBtnprefix?: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    selectAllRow: {
      marginBottom: theme.spacing(1),
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      padding: pxToRem(4, 0),
    },
    emptyMsg: {
      padding: pxToRem(8, 0),
      color: theme.palette.text.secondary,
    },
  }),
);

const BopsPreferencesChecklist = ({ options, value, onChange, qaBtnprefix }: BopsPreferencesChecklistProps) => {
  const classes = useStyles();

  const placeholder = value?.[0];
  const selected = (value ?? []).slice(1);

  const isChecked = (id: number) => selected.some((option) => option?.id === id);

  const toggle = (option: OptionType) => {
    const nextSelected = isChecked(option.id)
      ? selected.filter((selectedOption) => selectedOption.id !== option.id)
      : [...selected, { id: option.id, name: option.name }];
    onChange([placeholder as OptionType, ...nextSelected]);
  };

  const allSelected = options.length > 0 && options.every((option) => isChecked(option.id));
  const someSelected = options.some((option) => isChecked(option.id));

  const toggleSelectAll = () => {
    const nextSelected = allSelected ? [] : options.map((option) => ({ id: option.id, name: option.name }));
    onChange([placeholder as OptionType, ...nextSelected]);
  };

  if (!options || options.length === 0) {
    return (
      <Typography variant="body2" className={classes.emptyMsg}>
        There are no download preferences available.
      </Typography>
    );
  }

  return (
    <>
      <div className={classes.selectAllRow}>
        <AgentNetCheckBox
          label="Select All"
          name={`${qaBtnprefix ?? 'bopsPreference'}-select-all`}
          value={allSelected}
          indeterminate={someSelected && !allSelected}
          checkHandler={toggleSelectAll}
        />
      </div>
      <Grid container>
        {placeholder && (
          <Grid item sm={3} xs={12} className={classes.row}>
            <AgentNetCheckBox label={placeholder.name} value disabled />
          </Grid>
        )}
        {options.map((option) => (
          <Grid item sm={3} xs={12} key={option.id} className={classes.row}>
            <AgentNetCheckBox
              value={isChecked(option.id)}
              label={option.name}
              name={`${qaBtnprefix ?? 'bopsPreference'}-${option.id}`}
              checkHandler={() => toggle(option)}
            />
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export default BopsPreferencesChecklist;
